import React, { FC, useCallback, useEffect, useId, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import Slider from '@rover/components/src/formFields/Slider';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { PRICE_FILTER } from '@rover/constants/src/search/searchPage.constants';
import { Flex, Text } from '@rover/kibble/core';
import FilterFieldset from '@rover/shared/js/search/components/FilterFieldset';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { formatCurrency, getCurrencyAndLocaleConfig } from '@rover/utilities/currency';
import { getIsPriceTransparencyFull } from '@rover/utilities/priceTransparency';

import CurrencyInput from './CurrencyInput';

export type Props = {
  currencyCode: string;
  label?: string;
  locale: Locale;
  onChange: (values: { min: number; max: number }) => void;
  selectedRangeMax: number;
  selectedRangeMin: number;
  sliderMax?: number | null;
  sliderMin?: number | null;
  priceUnit?: string;
  isIncludingFees?: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  isStaffOrImpersonating?: boolean;
  showPriceInputs?: boolean;
};

export type State = {
  isDragging: boolean;
  maxHandleValue: number;
  minHandleValue: number;
};

const HANDLE_HEIGHT = 20;
const HANDLE_HEIGHT_MOBILE = 28;
const MIN_PRICE_GAP = 1;

const StyledSlider = styled(Slider)`
  flex-grow: 1;
  margin: 0 ${({ theme }) => theme.space['4x']};
`;

type CurrencyLabelProps = {
  value: number;
  currencyCode: string;
  locale: Locale;
  isIncludingFees?: boolean;
  isMaxValue?: boolean;
};

const CurrencyLabel = ({
  value,
  currencyCode,
  locale,
  isIncludingFees,
  isMaxValue,
}: CurrencyLabelProps): JSX.Element => {
  const formattedCurrencyLabel = formatCurrency(value, currencyCode, locale, {
    includeDecimal: false,
  });

  return (
    <Text size="100">
      {formattedCurrencyLabel}
      {isIncludingFees && isMaxValue && '+'}
    </Text>
  );
};

const PriceFilter: FC<Props> = ({
  currencyCode,
  label,
  locale,
  sliderMax = PRICE_FILTER.DEFAULT_MAX_PRICE,
  sliderMin = PRICE_FILTER.DEFAULT_MIN_PRICE,
  selectedRangeMax,
  selectedRangeMin,
  onChange,
  priceUnit = '',
  isIncludingFees,
  priceTransparencyLevel,
  isStaffOrImpersonating,
  showPriceInputs = false,
}) => {
  const { t } = useLingui();
  const priceSliderId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const [minHandleValue, setMinHandleValue] = useState(selectedRangeMin);
  const [maxHandleValue, setMaxHandleValue] = useState(selectedRangeMax);
  const [prevCurrencyCode, setPrevCurrencyCode] = useState(currencyCode);
  const [minInputValue, setMinInputValue] = useState(String(selectedRangeMin));
  const [maxInputValue, setMaxInputValue] = useState(String(selectedRangeMax));
  const sliderValuesWithinBounds = useCallback(
    (currentSliderValues: number[]): number[] => {
      const [selectedMin, selectedMax] = currentSliderValues;
      if (sliderMin == null || sliderMax == null) return currentSliderValues;

      // Bounds are present and the slider is outside of those, reset the slider to bounds
      if (
        selectedMin < sliderMin ||
        selectedMin > sliderMax ||
        selectedMax < sliderMin ||
        selectedMax > sliderMax
      ) {
        return [sliderMin, sliderMax];
      }

      return currentSliderValues;
    },
    [sliderMin, sliderMax]
  );

  const handleChange = useCallback(
    ({ values }: { values: number[] }): void => {
      const [$minHandleValue, $maxHandleValue] = sliderValuesWithinBounds(values);
      onChange({ max: $maxHandleValue, min: $minHandleValue });
      setMinHandleValue($minHandleValue);
      setMaxHandleValue($maxHandleValue);
      setIsDragging(false);
    },
    [onChange, sliderValuesWithinBounds]
  );

  const handleValuesUpdated = useCallback(({ values }: { values: number[] }): void => {
    const [$minHandleValue, $maxHandleValue] = values;
    setMinHandleValue($minHandleValue);
    setMaxHandleValue($maxHandleValue);
  }, []);

  const handleDragMove = useCallback((): void => {
    setIsDragging(true);
  }, []);

  const handleMinInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setMinInputValue(e.target.value);
  }, []);

  const handleMaxInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setMaxInputValue(e.target.value);
  }, []);

  const handleMinInputBlur = useCallback((): void => {
    const parsed = Number(minInputValue);
    const effectiveMin = sliderMin ?? PRICE_FILTER.DEFAULT_MIN_PRICE;

    let clamped: number;
    if (Number.isNaN(parsed) || minInputValue === '') {
      clamped = effectiveMin;
    } else {
      clamped = Math.max(effectiveMin, Math.min(parsed, selectedRangeMax - MIN_PRICE_GAP));
    }

    setMinInputValue(String(clamped));
    onChange({ min: clamped, max: selectedRangeMax });
  }, [minInputValue, selectedRangeMax, sliderMin, onChange]);

  const handleMaxInputBlur = useCallback((): void => {
    const parsed = Number(maxInputValue);
    const effectiveMax = sliderMax ?? PRICE_FILTER.DEFAULT_MAX_PRICE;

    let clamped: number;
    if (Number.isNaN(parsed) || maxInputValue === '') {
      clamped = effectiveMax;
    } else {
      clamped = Math.min(effectiveMax, Math.max(parsed, selectedRangeMin + MIN_PRICE_GAP));
    }

    setMaxInputValue(String(clamped));
    onChange({ min: selectedRangeMin, max: clamped });
  }, [maxInputValue, selectedRangeMin, sliderMax, onChange]);

  useEffect(() => {
    if (currencyCode !== prevCurrencyCode) {
      onChange({ max: sliderMax as number, min: sliderMin as number });
      setPrevCurrencyCode(currencyCode);
      setMinInputValue(String(sliderMin));
      setMaxInputValue(String(sliderMax));
    }
  }, [currencyCode, onChange, prevCurrencyCode, sliderMax, sliderMin]);

  useEffect(() => {
    setMinInputValue(String(selectedRangeMin));
    setMaxInputValue(String(selectedRangeMax));
  }, [selectedRangeMin, selectedRangeMax]);

  const isPtFull = getIsPriceTransparencyFull(priceTransparencyLevel);
  let actualLabel =
    label ||
    t({
      message: `Rate per ${priceUnit}`,
      comment: 'Rate per {priceUnit} e.g. day, night, walk',
    });
  if (isStaffOrImpersonating && isPtFull) {
    actualLabel += ' (STAFF ONLY)';
  }

  const [sliderRangeMin, sliderRangeMax] = sliderValuesWithinBounds([
    selectedRangeMin,
    selectedRangeMax,
  ]);

  const minHandlePrice = isDragging ? minHandleValue : sliderRangeMin;
  const maxHandlePrice = isDragging ? maxHandleValue : sliderRangeMax;

  const {
    currencyConfig: { symbol: currencySymbol },
  } = getCurrencyAndLocaleConfig(currencyCode, locale);

  return (
    <FilterFieldset aria-labelledby={priceSliderId}>
      <Flex flexDirection="column" id={priceSliderId}>
        <Text
          size="100"
          textColor="secondary"
          fontWeight="semibold"
          mb={isIncludingFees ? '1x' : '2x'}
        >
          {actualLabel}
        </Text>
        {isIncludingFees && (
          <Text size="100" textColor="tertiary" mb="2x">
            <Trans>including fees</Trans>
          </Text>
        )}
      </Flex>
      <Flex alignItems="center">
        <CurrencyLabel
          value={minHandlePrice}
          currencyCode={currencyCode}
          locale={locale}
          data-testid="min-value"
          isIncludingFees={isIncludingFees}
        />
        <StyledSlider
          data-testid="price-slider"
          handleHeight={HANDLE_HEIGHT}
          handleHeightMobile={HANDLE_HEIGHT_MOBILE}
          id={priceSliderId}
          max={sliderMax}
          min={sliderMin}
          onChange={handleChange}
          onSliderDragMove={handleDragMove}
          onValuesUpdated={handleValuesUpdated}
          values={[sliderRangeMin, sliderRangeMax]}
          prefix={currencyCode}
        />
        <CurrencyLabel
          value={maxHandlePrice}
          currencyCode={currencyCode}
          locale={locale}
          data-testid="max-value"
          isIncludingFees={isIncludingFees}
          isMaxValue={maxHandleValue === sliderMax}
        />
      </Flex>
      {showPriceInputs && (
        <Flex flexDirection="row" mt="4x" gap="4x">
          <CurrencyInput
            label={t`Min. per ${priceUnit}`}
            value={minInputValue}
            currencySymbol={currencySymbol}
            onChange={handleMinInputChange}
            onBlur={handleMinInputBlur}
            data-testid="min-price-input"
          />
          <CurrencyInput
            label={t`Max. per ${priceUnit}`}
            value={maxInputValue}
            currencySymbol={currencySymbol}
            onChange={handleMaxInputChange}
            onBlur={handleMaxInputBlur}
            data-testid="max-price-input"
          />
        </Flex>
      )}
    </FilterFieldset>
  );
};

export default PriceFilter;
